import { DestroyRef, inject, Inject, Injectable } from '@angular/core';
import {
  AccountsService,
  AppAndAddonActivationStatus,
  ListAppAndAddonActivationStatusFilter,
} from '@vendasta/accounts/legacy';
import { AccountsService as Accounts } from '@vendasta/accounts';
import { FeatureFlagMultiResponse, FeatureFlagService } from '@vendasta/businesses';
import {
  BillingFrequency,
  GetListingDataRequest,
  GetListingDataResponse,
  GetListingSyncProAddonIdRequestInterface,
  GetListingSyncProAddonIdResponse,
  IsListingSyncProActiveRequest,
  IsListingSyncProActiveResponse,
  ListingSyncProApiService as ListingSyncProApiServiceSDK,
  ServiceProvider,
} from '@vendasta/listing-sync-pro';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import {
  accuracyBadgeStyle,
  AGIDTOKEN,
  AppleSourceID,
  DEMO_PAID_EDITION,
  determineOriginalListingScore,
  DIRECT_SUBMIT_SOURCE_CONFIG,
  DirectSyncErrorMessage,
  FacebookSourceID,
  getSyncStatusText,
  GoogleSourceID,
  ListingsScoreSummaryData,
  LOCAL_SEO_UI_TRIGGER,
  MaterialIcons,
  PROD_PAID_EDITION,
  syncBadgeIcon,
  syncBadgeStyle,
  SyncData,
  SyncError,
  SyncingProgressSummaryData,
  Top4SourceID,
} from '../local-seo';
import { UpgradeCTADialogService } from '../upgrade-cta-dialog/upgrade-cta-dialog.service';
import { ListingSyncApiService } from './listing-sync.api.service';
import { ListingScoreApiService } from './listing-score.api.service';
import {
  GetEditionSourceConfigRequest,
  GetEditionSourceConfigResponse,
  SetAssociatedSocialServiceRequest,
  SubmitListingSyndicationRequest,
  SyndicationApiService,
  TurnOffSyndicationSettingRequest,
  TurnOnSyndicationSettingRequest,
} from '@vendasta/listing-syndication';
import {
  AccuracyStatus as LPAccuracyStatus,
  AnchorDataMatches,
  GetSyncDataRequest,
  ListingProductsApiService,
  SourceAccuracy,
  SyncData as SyncDataProto,
  SyncStatus,
} from '@vendasta/listing-products';
import { ListingAccuracyText } from './listing-sync-table/columns/presence-accuracy/accuracy-text.pipe';
import { TranslateService } from '@ngx-translate/core';
import { CategorySelectService } from '@galaxy/business-category';
import { LanguageService } from './language-service.service';
import { getLastUpdated, SITE_TYPE_MAPPING } from './listing-sync-table/listing-sync-table.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class ListingSyncService {
  public addonID$: Observable<string | null>;

  private listingDataRetry$$ = new Subject<null>();
  public listingData$: Observable<GetListingDataResponse>;

  private syncDataState$$: BehaviorSubject<SyncDataProto[]> = new BehaviorSubject<SyncDataProto[]>([]);
  public syncData$: Observable<SyncData[]> = this.syncDataState$$.asObservable().pipe(
    switchMap((data) => {
      return combineLatest(data.map((src) => this.constructSyncData(src)));
    }),
  );
  private syncDataRetry$$ = new Subject();

  private loadingSource$$ = new BehaviorSubject<number>(0);
  public readonly loadingSource$: Observable<number> = this.loadingSource$$.asObservable();

  private lspActivation$: Observable<IsListingSyncProActiveResponse>;
  public lspActive$: Observable<boolean>;
  public activeLSPProvider$: Observable<ServiceProvider>;

  public isLBFreeEdition$: Observable<boolean>;
  public featureFlags$: Observable<FeatureFlagMultiResponse>;

  public syncingProgressSummaryData$: Observable<SyncingProgressSummaryData>;
  public listingsScoreSummaryData$: Observable<ListingsScoreSummaryData>;
  public lspFacebookPages$: Observable<SyncData>;

  public listingSyncUnavailable$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public listingScoreUnavailable$: Observable<boolean>;

  private activeProductIds$: Observable<string[]>;
  private freeSources$: Observable<number[]>;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(AGIDTOKEN) private agid$: Observable<string>,
    private apiService: ListingSyncApiService,
    private listingSyncProApiServiceSDK: ListingSyncProApiServiceSDK,
    private legacyAccountsService: AccountsService,
    private accountsService: Accounts,
    private upgradeCTADialogService: UpgradeCTADialogService,
    private featureFlagService: FeatureFlagService,
    private listingScoreApiService: ListingScoreApiService,
    private listingSyndicationService: SyndicationApiService,
    private listingProductsService: ListingProductsApiService,
    private translate: TranslateService,
    private categorySelectService: CategorySelectService,
    private languageService: LanguageService,
  ) {
    this.loadSyncData(0);
    this.listingData$ = this.listingDataRetry$$.pipe(
      startWith(null),
      switchMap(() =>
        this.agid$.pipe(
          switchMap((agid: string) => {
            const data = this.listingSyncProApiServiceSDK
              .getListingData(new GetListingDataRequest({ businessId: agid }))
              .pipe(
                catchError(() => {
                  // Return an empty GetListingDataResponse if the API call fails
                  this.listingSyncUnavailable$$.next(true);
                  return of(new GetListingDataResponse({}));
                }),
              );
            return data;
          }),
        ),
      ),
      shareReplay(1),
    );

    this.lspFacebookPages$ = this.syncData$.pipe(
      map((data) => data?.find((source) => source.sourceId === FacebookSourceID)),
    );

    this.featureFlags$ = upgradeCTADialogService.partnerMarket$.pipe(
      switchMap((partnerMarket) =>
        featureFlagService.checkFeatureFlagsMulti(partnerMarket.partnerId, partnerMarket.marketId, [
          'reauth_facebook_account_banner_2023',
          'apple_oauth_connection',
        ]),
      ),
      shareReplay(1),
    );

    // We just want to catch errors for the listing score. I.E. the dreaded elasticsearch failure
    this.listingScoreUnavailable$ = this.agid$.pipe(
      switchMap((agid) => {
        return this.listingScoreApiService.getListingScore(agid, []).pipe(
          shareReplay(1),
          catchError((e) => {
            return of({ error: e });
          }),
          map((r) => !!r.error),
        );
      }),
    );

    this.isLBFreeEdition$ = this.agid$.pipe(
      switchMap((agid) => {
        const filters: ListAppAndAddonActivationStatusFilter = {
          appIds: ['MS'],
          statuses: [AppAndAddonActivationStatus.ACTIVATED, AppAndAddonActivationStatus.CANCELED],
        };
        return legacyAccountsService.listAppsAndAddonsActivationStatusesForBusiness(agid, filters).pipe(
          map((activations) => {
            if (activations?.length > 0) {
              switch (activations[0].editionId) {
                case DEMO_PAID_EDITION:
                  return false;
                case PROD_PAID_EDITION:
                  return false;
              }
            }
            return true;
          }),
          shareReplay(1),
        );
      }),
    );

    this.addonID$ = this.agid$.pipe(
      switchMap((agid) =>
        this.listingSyncProApiServiceSDK.getListingSyncProAddonId({
          accountGroupId: agid,
          provider: ServiceProvider.SERVICE_PROVIDER_UNSPECIFIED,
          billingFrequency: BillingFrequency.BILLING_FREQUENCY_MONTHLY,
        } as GetListingSyncProAddonIdRequestInterface),
      ),
      catchError(() => of(null)),
      map((resp: GetListingSyncProAddonIdResponse) => (resp && resp.addonId ? resp.addonId : null)),
      shareReplay(1),
    );

    this.lspActivation$ = this.agid$.pipe(
      switchMap((agid) =>
        this.listingSyncProApiServiceSDK.isListingSyncProActiveForAccount(
          new IsListingSyncProActiveRequest({
            accountGroupId: agid,
          }),
        ),
      ),
      catchError(() => of(null)),
      shareReplay(1),
    );

    this.lspActive$ = this.lspActivation$.pipe(map((resp: IsListingSyncProActiveResponse) => resp?.isActive));

    this.activeLSPProvider$ = this.lspActivation$.pipe(
      map((data) => (data.isActive ? data.provider : ServiceProvider.SERVICE_PROVIDER_UNSPECIFIED)),
    );

    this.syncingProgressSummaryData$ = this.syncData$.pipe(
      map((data) => {
        return <SyncingProgressSummaryData>{
          totalSourcesCount:
            data
              ?.map((s) => {
                return 1 + s.ecosystemSources?.length;
              })
              .reduce((sum, current) => sum + current, 0) || 0,
          syncedSourcesCount:
            data
              ?.map((s) => {
                return s.syncStatus === SyncStatus.SYNC_STATUS_SUCCESS ||
                  s.syncStatus === SyncStatus.SYNC_STATUS_SUBMITTED
                  ? 1 + s.ecosystemSources?.length
                  : 0;
              })
              .reduce((sum, current) => sum + current, 0) || 0,
          syncInProgressSourcesCount:
            data
              ?.map((s) => {
                const inProgress =
                  s.syncStatus === SyncStatus.SYNC_STATUS_SYNCING ||
                  s.syncStatus === SyncStatus.SYNC_STATUS_PARTIALLY_SYNCED;
                return inProgress ? 1 + s.ecosystemSources?.length : 0;
              })
              .reduce((sum, current) => sum + current, 0) || 0,
          notSyncingSourcesCount:
            data
              ?.map((s) => {
                const notSyncing =
                  s.syncStatus === SyncStatus.SYNC_STATUS_UNAVAILABLE ||
                  s.syncStatus === SyncStatus.SYNC_STATUS_UNSET ||
                  s.syncStatus === SyncStatus.SYNC_STATUS_FAILED ||
                  s.syncStatus === SyncStatus.SYNC_STATUS_CLAIMED;
                return notSyncing ? 1 + s.ecosystemSources?.length : 0;
              })
              .reduce((sum, current) => sum + current, 0) || 0,
        };
      }),
      shareReplay(1),
    );

    this.listingsScoreSummaryData$ = this.listingData$.pipe(
      map((data) => {
        return {
          listingScore: data?.scores?.totalScore ? data.scores?.totalScore : 0,
          totalAcceptedSources: data?.totalAcceptedSources ? data.totalAcceptedSources : 0,
          totalSources: data?.totalSources ? data.totalSources : 0,
          originalListingScore: determineOriginalListingScore(data?.scores),
          industryAverageListingsScore: data?.scores?.industryAverage ? data.scores?.industryAverage : 0,
          ninetyFifthPercentile: data?.scores?.ninetyFifthPercentile ? data.scores?.ninetyFifthPercentile : 0,
          totalAccurate: data?.totalAccuracyGoodSources ? data.totalAccuracyGoodSources : 0,
          totalPossibleErrors: data?.totalPossibleErrors ? data.totalPossibleErrors : 0,
          totalNotFound: data?.totalNotFound ? data.totalNotFound : 0,
          history:
            data?.scores?.listingScoreHistory?.map((history) => {
              return {
                name: history?.timestamp,
                value: history?.score || 0,
              };
            }) || [],
        };
      }),
    );

    this.activeProductIds$ = this.agid$.pipe(
      switchMap((businessID) => this.accountsService.getMultiActivationStatuses([businessID])),
      map((activations) => activations.map((activation) => activation.productId)),
      shareReplay(1),
    );

    this.freeSources$ = this.upgradeCTADialogService.partnerMarket$.pipe(
      switchMap((pm) =>
        this.listingSyndicationService.getEditionSourceConfig(
          new GetEditionSourceConfigRequest({
            namespace: pm.partnerId,
          }),
        ),
      ),
      map((resp: GetEditionSourceConfigResponse) => {
        return resp?.freeSourceIds || [];
      }),
      shareReplay(1),
    );
  }

  loadSyncData(sourceId: number): void {
    this.loadingSource$$.next(sourceId);
    this.agid$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((agid) => {
          return this.listingProductsService.getSyncData(
            new GetSyncDataRequest({
              businessId: agid,
              refresh: true,
            }),
          );
        }),
      )
      .subscribe((res) => {
        this.syncDataState$$.next(res?.sources || []);
        this.loadingSource$$.next(0);
      });
  }

  updateSyncStatus(sourceId: number, status: SyncStatus, syncingEnabled: boolean): void {
    const sources = this.syncDataState$$.value.map((src) => {
      if (+src.sourceId === sourceId) {
        src.syncStatus = status;
        src.isSyncingEnabled = syncingEnabled;
      }
      return src;
    });
    this.syncDataState$$.next(sources);
  }

  disconnectService$(url: string, ssid: string, clientTags = []): Observable<any> {
    return this.apiService.disconnectService(url, ssid, clientTags);
  }

  constructSyncData(src: SyncDataProto): Observable<SyncData> {
    // If the account does not have a profile URL, set it to the listing URL
    if (
      src.account &&
      !src.account.profileUrl &&
      src.listingUrl &&
      (+src.sourceId === GoogleSourceID || +src.sourceId === AppleSourceID)
    ) {
      src.account.profileUrl = src.listingUrl;
    }

    const errors =
      src?.syncDetails?.detailedResults
        ?.filter((result) => {
          return result?.value?.errorMessage;
        })
        .map((result) => {
          return new DirectSyncErrorMessage(
            this.translate,
            this.categorySelectService,
            this.languageService,
            result?.value?.errorMessage,
          );
        }) || [];

    let errorMessages$: Observable<SyncError[]> = of([]);
    if (errors.length > 0) {
      errorMessages$ = combineLatest(errors.map((error) => error.translatedError$));
    }
    return errorMessages$.pipe(
      map((errorMessages) => {
        const config = DIRECT_SUBMIT_SOURCE_CONFIG[+src.sourceId];

        // TODO: Remove when Top4 accuracy is implemented
        if (+src.sourceId === Top4SourceID && src.isSyncingEnabled) {
          src.score = 2;
        }

        return {
          sourceId: +src.sourceId,
          name: src.name,
          accuracy: new SourceAccuracy({
            anchorData: src.accuracy?.anchorData,
            anchorDataMatches: src.accuracy?.anchorDataMatches || new AnchorDataMatches({}),
            status: src.accuracy?.status,
          }),
          score: src.score || 0,
          maxScore: src.maxScore,
          listingUrl: src.listingUrl,
          listingId: src.listingId,
          sourceIcon: src.iconUrl,

          sourceHasReporting: src.accuracy?.status !== LPAccuracyStatus.ACCURACY_STATUS_UNAVAILABLE,
          account: src.account,
          additionalAccounts: src.additionalAccounts,
          connectLink: src.connectLink,
          clientTags: src.account?.clientTags,

          syncDetails: src.syncDetails,
          syncStatus: src.syncStatus || 0,
          isSyncingEnabled: src.isSyncingEnabled,
          isListingVerified: src.isListingVerified,
          errors: errors,
          hidden: src.hidden,
          provider: src.provider,
          requirement: src.requirement,
          sourceType: this.translate.instant(SITE_TYPE_MAPPING.get(src.sourceType || 'directory-site')),
          ecosystemSources: src.ecosystemSources || [],

          errorMessages: errorMessages,
          syncInfo: {
            icon: syncBadgeIcon(src),
            style: syncBadgeStyle(src),
            text: getSyncStatusText(
              this.translate,
              +src.sourceId,
              src.name,
              src.listingUrl && src.listingUrl.length > 0,
              src.accuracy.status,
              src.account?.connectionId?.length > 0 || !config?.RequiresAccount,
              src.isSyncingEnabled,
              src.locked,
              src.account?.tokenBroken,
              src.account?.connectionId?.length > 0 && src.account.isVerified,
              src.account?.connectionId && !src.account?.isAuthenticated,
              src.syncStatus || 0,
            ),
          },
          accuracyInfo: {
            style: accuracyBadgeStyle(src),
            text: this.translate.instant(ListingAccuracyText[src.accuracy?.status]?.Card),
            icon:
              src.accuracy?.status === LPAccuracyStatus.ACCURACY_STATUS_ACCURATE
                ? MaterialIcons.DONE
                : MaterialIcons.NONE,
          },
          locked: src.locked,
          lastUpdatedDate: src.accuracy?.lastScraped || src?.syncDetails?.lastAttemptedOn,
          lastUpdated: getLastUpdated(src.accuracy?.lastScraped || src?.syncDetails?.lastAttemptedOn),
        };
      }),
    );
  }

  updateSyncing(sourceId: number, ssid: string, newSyncState: boolean): Observable<any> {
    ssid = newSyncState ? ssid : '';

    let request: Observable<any>;
    const syndicationSettingRequest = this.agid$.pipe(
      switchMap((agid) => {
        if (newSyncState) {
          const turnOnSyndicationRequest = new TurnOnSyndicationSettingRequest({
            businessId: agid,
            sourceId: sourceId.toString(),
            associatedSocialServiceId: ssid,
          });
          return this.listingSyndicationService.turnOnSyndication(turnOnSyndicationRequest);
        }

        return this.listingSyndicationService.turnOffSyndication(
          new TurnOffSyndicationSettingRequest({
            businessId: agid,
            sourceId: sourceId.toString(),
          }),
        );
      }),
    );
    if (newSyncState) {
      const syncRequest = this.agid$.pipe(
        switchMap((agid) => {
          return this.listingSyndicationService.submitListingSyndication(
            new SubmitListingSyndicationRequest({
              accountGroupId: agid,
              sourceIds: [sourceId],
              trigger: LOCAL_SEO_UI_TRIGGER,
            }),
          );
        }),
      );
      request = syndicationSettingRequest.pipe(switchMap(() => syncRequest));
    } else {
      request = syndicationSettingRequest;
    }

    return request;
  }

  setSelectedSocialService(selectedSSID: string, sourceId: number): Observable<any> {
    return this.agid$.pipe(
      switchMap((agid) => {
        return this.listingSyndicationService
          .setAssociatedSocialService(
            new SetAssociatedSocialServiceRequest({
              businessId: agid,
              sourceId: sourceId.toString(),
              associatedSocialServiceId: selectedSSID,
            }),
          )
          .pipe(switchMap(() => this.updateSyncing(sourceId, '', false)));
      }),
    );
  }
}
